import { ErrorMessage } from "@/common/components/ErrorMessage"
import { ImageCarousel } from "@/common/components/ImageCarousel"
import { cn } from "@/common/utils/cn"
import { checkIsImage } from "@/common/utils/fileValidator"
import { replacePeriodToUnderscore } from "@/common/utils/transformers"
import { UploadIcon } from "lucide-react"
import { BaseSyntheticEvent, useState } from "react"
import { ArrayPath, Control, FieldArrayWithId, FieldValues, useFieldArray } from "react-hook-form"
import { twMerge } from "tailwind-merge"

export interface MultipleImageUploaderProps<T extends FieldValues> extends React.HTMLAttributes<HTMLInputElement> {
  label: string | JSX.Element
  name: string
  accept?: string
  uploadBlob: (data: { file: File; filename: string }) => Promise<any>
  onRemove?: (id: string) => Promise<void>
  submit?: (e?: BaseSyntheticEvent) => Promise<void>
  control: Control<T, any>
  fieldName: ArrayPath<T>
  disabled?: boolean
  errorMessage?: string
  isLoading?: boolean
  hasPermission?: boolean
  inputClassName?: string
  iconClassName?: string
  containerClassName?: string
  uploadButtonLayout?: "block" | "floating"
  imageCarouselClassName?: string;
  imageClassName?: string
}

interface UploadedFile {
  id: string
  file: File
}

export const MultipleImageUploader = <T extends FieldValues>({
  label,
  name,
  uploadBlob,
  onRemove,
  submit,
  control,
  fieldName,
  disabled,
  errorMessage,
  isLoading,
  hasPermission = true,
  inputClassName,
  iconClassName,
  containerClassName,
  uploadButtonLayout = "block",
  imageCarouselClassName,
  imageClassName,
  ...props
}: MultipleImageUploaderProps<T>) => {
  const [isRemoving, setIsRemoving] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  const {
    fields: files,
    append: appendFile,
    remove: removeFile,
  } = useFieldArray({
    control,
    name: fieldName,
    keyName: '_id',
  })

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter((file) => checkIsImage(file))

    if (files.length > 0) {
      for (const file of files) {
        const data = await uploadBlob({ file, filename: file.name })

        if (data?.data?.data?.id) {
          appendFile({ id: data.data.data.id, file } as unknown as FieldArrayWithId<T, ArrayPath<T>>)

          submit && submit()
        }
      }
    }
    e.target.value = ''
  }

  const handleRemoveImage = async (currentIndex: number) => {
    const removedFile = files[currentIndex] as unknown as UploadedFile;
    removeFile(currentIndex)

    if (removedFile?.id && onRemove) {
      setIsRemoving(true)
      await onRemove(removedFile.id)
      setIsRemoving(false)
    }

    submit && submit()
  }

  return (
    <div className={twMerge("space-y-2", containerClassName)}>
      <div className="flex flex-wrap gap-4">
        {files.length > 0 && (
          <ImageCarousel
            files={(files as unknown as UploadedFile[]).map((file) => ({ id: file.id, file: file.file }))}
            name={name}
            handleRemoveImage={handleRemoveImage}
            isLoading={isLoading || isRemoving}
            showRemoveButton={hasPermission}
            showExpandButton
            handleImageUpload={uploadButtonLayout === "floating" ? handleImageUpload : undefined}
            inputFileIsDisabled={disabled || isLoading}
            className={imageCarouselClassName}
            imageClassName={imageClassName}
          />
        )}
        {hasPermission && (uploadButtonLayout === "block" || files.length === 0) && (
          <>
            <label
              className="relative cursor-pointer"
              htmlFor={name}
              onDragLeave={e => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                  setDragActive(false)
                }
              }}
              onDragEnter={e => { e.preventDefault(); setDragActive(true) }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault(); setDragActive(false);
                handleImageUpload({ target: { files: e.dataTransfer.files } } as any)
              }}
            >
              <div
                className={cn(
                  'w-72 h-72 flex justify-center items-center cursor-pointer rounded-lg p-4 border-2 border-dashed transition duration-200 ease-in-out',
                  dragActive
                    ? 'bg-blue-100/30 border-primary-blue'
                    : 'bg-white border-gray-300 hover:border-primary-blue',
                  inputClassName,
                )}
              >
                <div className="flex flex-col items-center gap-y-2">
                  {typeof label === 'string' ? (<p className="text-xs">{label}</p>) : label}
                  <UploadIcon className={cn('w-8 h-8 text-primary-blue', iconClassName)} strokeWidth={1} />
                  <p className="font-light text-xs italic">{dragActive ? "Solte aqui" : 'Ou arraste até aqui'}</p>
                </div>
              </div>

            </label>
            <input type="file" name={name} disabled={disabled || isLoading} className="hidden" id={name} multiple onChange={handleImageUpload} data-testid={name ? replacePeriodToUnderscore(name) + '_input' : ''} {...props} />
          </>
        )}
      </div>
      {errorMessage && <ErrorMessage name={name} message={errorMessage} />}
    </div>
  )
}
